import React, { useEffect, useState } from "react";
import {
    Button,
    makeStyles,
    tokens,
} from "@fluentui/react-components";
import { Pivot, PivotItem } from "@fluentui/react";
import {
    CheckmarkCircleFilled,
    DismissCircleRegular,
} from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { APPS_BY_CATEGORY, AZURE_APPS } from "../../Config/ConsentConfig";
import { getAdminConsentUrl, registerConsentApi, checkConsentStatusApi } from "../../Services/HeedifyServices";
import { getAccessToken } from "../../Services/GraphServices";
import { useAbility } from "../../Contexts/AbilityContext";
import InfoButton from "../GenericComponent/InfoButton";
import "../CSS/GlobalSettingsComponent.css";

const useStyles = makeStyles({
    successIcon: {
        color: tokens.colorPaletteGreenForeground1,
        fontSize: "24px",
        marginRight: "8px",
    },
    disabledIcon: {
        color: tokens.colorNeutralForegroundDisabled,
        fontSize: "24px",
        marginRight: "8px",
    },
});

const GlobalSettingsComponent = (props) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const { accounts, instance } = useMsal();
    const activeAccount = accounts?.[0];
    const tenantId = props.ACTIVE_TENANT;
    const userId = activeAccount?.localAccountId;
    const ability = useAbility();
    
    // State for active tab
    const [selectedTab, setSelectedTab] = useState("commun");
    
    // Utiliser le scope actif ou "default" si non défini
    const scope = props.store_activeScope || "default";

    // Permissions pour GlobalSettings avec le scope
    const permissions = {
        main: {
            canRead: ability?.can("read", `GlobalSettings.main@${scope}`),
            canCreate: ability?.can("create", `GlobalSettings.main@${scope}`),
            canUpdate: ability?.can("update", `GlobalSettings.main@${scope}`),
            canDelete: ability?.can("delete", `GlobalSettings.main@${scope}`)
        }
    };

    // Charger le statut des consents au montage du composant
    useEffect(() => {
        if (!tenantId || !userId || !instance || !activeAccount) return;
        
        getAccessToken(instance, activeAccount)
            .then((token) => {
                return checkConsentStatusApi(tenantId, userId, token);
            })
            .then((result) => {
                if (result?.consentedApp) {
                    props.setConsentedApps(result.consentedApp);
                }
            })
            .catch((error) => {
                console.error("Error loading consent status:", error);
            });
    }, [tenantId, userId, instance, activeAccount]);

    // Check for consent callback in URL
    useEffect(() => {
        if (!tenantId || !userId || !instance || !activeAccount) return;

        const params = new URLSearchParams(window.location.search);
        const adminConsent = params.get("admin_consent");
        const tenant = params.get("tenant");
        const currentPath = window.location.pathname;
        if (adminConsent === "True" && tenant) {
            // Find which app was consented based on the redirect path
            const consentedApp = Object.values(AZURE_APPS).find(
                (app) => app.redirectPath === currentPath
            );

            if (consentedApp) {
                // Register the consent and wait before redirecting
                const registerConsent = async () => {
                    try {
                        const token = await getAccessToken(instance, activeAccount);
                        await registerConsentApi(tenantId, userId, consentedApp.clientId, token);
                        const updatedConsents = [...(props.consentedApps || []), consentedApp.clientId];
                        props.setConsentedApps(updatedConsents);
                    } catch (error) {
                        console.error("Error registering consent:", error);
                    } finally {
                        window.location.href = "/globalSettings";
                    }
                };

                registerConsent();
            } else {
                window.location.href = "/globalSettings";
            }
        }
    }, [tenantId, userId, instance, activeAccount]);

    // Check if an app is consented - use clientId not id!
    const isAppConsented = (clientId) => {
        const isConsented = props.consentedApps && props.consentedApps.indexOf(clientId) >= 0;
        return isConsented;
    };
    // Render consent status icon
    const ConsentStatusIcon = ({ clientId }) => {
        return isAppConsented(clientId) ? (
            <CheckmarkCircleFilled className={styles.successIcon} />
        ) : (
            <DismissCircleRegular className={styles.disabledIcon} />
        );
    };

    // Get full redirect URI for consent callback
    const getRedirectUri = (redirectPath) => {
        return `${window.location.origin}${redirectPath}`;
    };

    // Si pas de permission de lecture, ne rien afficher
    if (!permissions.main.canRead) {
        return (
            <div className="access-denied-message">
                <h2>{t("GLOBAL_SETTINGS.ACCESS_DENIED")}</h2>
                <p>{t("GLOBAL_SETTINGS.NO_PERMISSION_TO_VIEW")}</p>
            </div>
        );
    }

    return (
        <div className="global-settings-container">
            <div className="global-settings-header">
                <h1 className="global-settings-title">{t("GLOBAL_SETTINGS.CONSENT_TITLE")}</h1>
                <InfoButton infoKey="CONSENT_TITLE" category="GLOBAL_SETTINGS" />
            </div>

            <Pivot 
                className="global-settings-pivot"
                selectedKey={selectedTab}
                onLinkClick={(item) => setSelectedTab(item.props.itemKey)}
            >
                <PivotItem headerText={t("GLOBAL_SETTINGS.COMMUN")} itemKey="commun">
                    <div className="apps-container">
                        {APPS_BY_CATEGORY.COMMUN.map((app) => (
                            <div key={app.id} className="app-item">
                                <div className="app-info">
                                    <div className="app-name-section">
                                        <span className="app-name">{t(`GLOBAL_SETTINGS.${app.name}`)}</span>
                                        <InfoButton infoKey={app.name} category="GLOBAL_SETTINGS" />
                                    </div>
                                </div>
                                <div className="app-actions">
                                    <ConsentStatusIcon clientId={app.clientId} />
                                    <Button
                                        appearance="primary"
                                        as="a"
                                        href={getAdminConsentUrl(
                                            app.clientId,
                                            getRedirectUri(app.redirectPath)
                                        )}
                                        rel="noopener noreferrer"
                                        disabled={!permissions.main.canUpdate}
                                    >
                                        {t("GLOBAL_SETTINGS.CONSENT_PERMISSIONS")}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </PivotItem>

                <PivotItem headerText={t("GLOBAL_SETTINGS.EXTEND")} itemKey="extend">
                    <div className="apps-container">
                        {APPS_BY_CATEGORY.EXTEND.map((app) => (
                            <div key={app.id} className="app-item">
                                <div className="app-info">
                                    <div className="app-name-section">
                                        <span className="app-name">{t(`GLOBAL_SETTINGS.${app.name}`)}</span>
                                        <InfoButton infoKey={app.name} category="GLOBAL_SETTINGS" />
                                    </div>
                                </div>
                                <div className="app-actions">
                                    <ConsentStatusIcon clientId={app.clientId} />
                                    <Button
                                        appearance="primary"
                                        as="a"
                                        href={getAdminConsentUrl(
                                            app.clientId,
                                            getRedirectUri(app.redirectPath)
                                        )}
                                        rel="noopener noreferrer"
                                        disabled={!permissions.main.canUpdate}
                                    >
                                        {t("GLOBAL_SETTINGS.CONSENT_PERMISSIONS")}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </PivotItem>

                <PivotItem headerText={t("GLOBAL_SETTINGS.TPE")} itemKey="tpe">
                    <div className="apps-container">
                        {APPS_BY_CATEGORY.TPE.map((app) => (
                            <div key={app.id} className="app-item">
                                <div className="app-info">
                                    <div className="app-name-section">
                                        <span className="app-name">{t(`GLOBAL_SETTINGS.${app.name}`)}</span>
                                        <InfoButton infoKey={app.name} category="GLOBAL_SETTINGS" />
                                    </div>
                                </div>
                                <div className="app-actions">
                                    <ConsentStatusIcon clientId={app.clientId} />
                                    <Button
                                        appearance="primary"
                                        as="a"
                                        href={getAdminConsentUrl(
                                            app.clientId,
                                            getRedirectUri(app.redirectPath)
                                        )}
                                        rel="noopener noreferrer"
                                        disabled={!permissions.main.canUpdate}
                                    >
                                        {t("GLOBAL_SETTINGS.CONSENT_PERMISSIONS")}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </PivotItem>
            </Pivot>
        </div>
    );
};

export default GlobalSettingsComponent;
